import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { GREEN_PILL_STATS, GREEN_TERMINAL_LINES, MATRIX_GREEN } from '../constants';

function useAnimatedPing(active: boolean) {
  const [ping, setPing] = useState(3);
  useEffect(() => {
    if (!active) return undefined;
    const interval = setInterval(() => {
      setPing(Math.round((1 + Math.random() * 4) * 10) / 10);
    }, 900);
    return () => clearInterval(interval);
  }, [active]);
  return ping;
}

function AnimatedTerminalLine({ label, value, status, delay, active }: { label: string; value: string; status: string; delay: number; active: boolean }) {
  const [displayed, setDisplayed] = useState('');
  const [started, setStarted] = useState(false);
  const fullText = `> ${label}: ${value} [${status}]`;

  useEffect(() => {
    if (!active) return undefined;
    const timer = setTimeout(() => setStarted(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [active, delay]);

  useEffect(() => {
    if (!started) return undefined;
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      setDisplayed(fullText.slice(0, i));
      if (i >= fullText.length) clearInterval(interval);
    }, 25);
    return () => clearInterval(interval);
  }, [started, fullText]);

  return (
    <div className='py-0.5 font-mono text-[11px] leading-relaxed md:text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN, minHeight: '1.4em' }}>
      {displayed}
      {started && displayed.length < fullText.length && (
        <motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.4, repeat: Infinity }} style={{ color: MATRIX_GREEN }}>
          _
        </motion.span>
      )}
    </div>
  );
}

export function GreenPillCard() {
  const { t } = useTranslation(['components/trial/lp/v2c']);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const animatedPing = useAnimatedPing(inView);

  useEffect(() => {
    if (!inView) return undefined;
    const interval = setInterval(() => {}, 1200);
    return () => clearInterval(interval);
  }, [inView]);

  const dynamicTerminalLines = GREEN_TERMINAL_LINES.map((line) => {
    if (line.label === 'PING') return { ...line, value: `${animatedPing}ms` };
    if (line.label === 'JITTER') {
      const jitter = (0.5 + Math.random() * 1.5).toFixed(1);
      return { ...line, value: `${jitter}ms` };
    }
    return line;
  });

  return (
    <div ref={ref} className='relative flex h-full flex-col justify-between overflow-hidden rounded-xl border p-6 md:ms-4 md:p-8' style={{ backgroundColor: `${MATRIX_GREEN}06`, borderColor: `${MATRIX_GREEN}30` }}>
      <div className='absolute inset-x-0 top-0 h-1' style={{ background: `linear-gradient(90deg, transparent, ${MATRIX_GREEN}, transparent)` }} />

      <div className='mb-6 font-mono text-xs uppercase tracking-wider' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN }}>
        {t('green_pill.header')}
      </div>

      <h3 className='mb-6 text-xl font-bold md:text-2xl' style={{ fontFamily: 'var(--font-bai)', color: MATRIX_GREEN }}>
        {t('green_pill.title')}
      </h3>

      <div className='space-y-4'>
        {GREEN_PILL_STATS.map((item, i) => (
          <div key={i} className='flex items-center justify-between border-b py-2 font-mono text-sm' style={{ fontFamily: 'var(--font-jetbrains), monospace', borderColor: `${MATRIX_GREEN}15` }}>
            <span style={{ color: `${MATRIX_GREEN}90` }}>{t(`green_pill.stats.${i}.label`)}</span>
            <span className='font-bold' style={{ color: MATRIX_GREEN }}>
              {item.label === 'Average Ping' ? (
                <motion.span key={animatedPing} initial={{ opacity: 0.5, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
                  {animatedPing}ms
                </motion.span>
              ) : (
                t(`green_pill.stats.${i}.value`)
              )}
            </span>
          </div>
        ))}
      </div>

      <div className='mt-4 rounded-lg border p-3 md:p-4' style={{ backgroundColor: `${MATRIX_GREEN}08`, borderColor: `${MATRIX_GREEN}25` }}>
        {dynamicTerminalLines.map((line, i) => (
          <AnimatedTerminalLine key={line.label} label={line.label} value={line.value} status={line.status} delay={0.3 + i * 0.12} active={inView} />
        ))}
      </div>

      <div className='mt-4 text-center font-mono text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: `${MATRIX_GREEN}60` }}>
        <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 2, repeat: Infinity }}>
          {'>'} STATUS: LIBERATED. PING: <span style={{ color: MATRIX_GREEN }}>{animatedPing}ms</span>
        </motion.span>
      </div>
    </div>
  );
}
